import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { X, Plus, Trash2, AlertCircle, CheckCircle2, ChevronDown, ChevronRight, Copy, Loader2, FileText, Package, Check, Database, PenTool, Lightbulb, FlaskConical, Sparkles } from 'lucide-react';
import { needsLiterConversion } from '@/lib/unitConverter';
import PicSelector from '@/components/shared/PicSelector';
import SearchableCombobox from '@/components/ui/SearchableCombobox';
import { toast } from 'sonner';
import { useSession } from '@/lib/SessionContext';
import { useAsaPersonality } from '@/hooks/useAsaPersonality';

const KATEGORI_BAHAN = ['Bibit', 'Alkohol', 'Aqua Des', 'DPG', 'Peg', 'Sustain', 'Quidex', 'Castor Oil', 'Lainnya'];
const PARFAROME_GRADE = ['Deluxe', 'Premium', 'Exclusive'];
const KATEGORI_SAMPLE = ['SPL', 'Tes Aroma', 'Lainnya'];
const LABEL_OPTIONS = ['A', 'B', 'C', 'D', 'E', 'F'];

const emptyBahan = () => ({ kategori_bahan: 'Bibit', vendor: '', nama_bahan: '', persentase: '', volume_ml: 0, dari_client: false });

const emptyProduct = (idx) => ({
  _id: Date.now() + idx,
  nama_produk: '',
  kode_produk: '',
  ukuran_ml: 20,
  label: 'A', // Start with A by default
  botol_id: '',
  botol_table: 'Fullset',
  botol_name: '',
  botol_dari_client: false,
  racikan: [],
  status: 'pending',
  catatan_review: '',
});

// Parse legacy single-product record into products[] array
function legacyToProducts(item) {
  if (item?.products && Array.isArray(item.products) && item.products.length > 0) {
    return item.products.map((p, i) => ({ ...p, _id: p._id || Date.now() + i }));
  }
  if (item?.nama_produk) {
    return [{
      _id: Date.now(),
      nama_produk: item.nama_produk || '',
      kode_produk: item.kode_produk || '',
      ukuran_ml: item.ukuran_ml || '',
      label: item.label || 'A',
      racikan: Array.isArray(item.racikan) ? item.racikan : [],
      status: item.status || 'pending',
      catatan_review: item.catatan_review || '',
    }];
  }
  return [emptyProduct(0)];
}

// Helper: parse decimal string that may use comma (Indonesian locale) or dot
const parseDecimal = (v) => {
  if (v === '' || v === null || v === undefined) return 0;
  return Number(String(v).replace(',', '.')) || 0;
};

// Helper: format number with max 2 decimal places in Indonesian format
const formatDecimalNumber = (val) => {
  if (val === null || val === undefined || isNaN(val)) return '0';
  const num = Number(val);
  return num.toLocaleString('id-ID', { maximumFractionDigits: 2, minimumFractionDigits: 0 });
};

// Helper to extract volume (ml) from item name or size fields
function extractVolumeFromItem(item) {
  if (!item) return null;
  if (item.ukuran_ml && !isNaN(Number(item.ukuran_ml))) return Number(item.ukuran_ml);
  if (item.ukuran && !isNaN(Number(item.ukuran))) return Number(item.ukuran);
  const text = `${item.nama || ''} ${item.nama_item || ''} ${item.deskripsi || ''}`;
  const match = text.match(/(\d+(?:[.,]\d+)?)\s*(?:ml|mili|milliliter)/i);
  if (match) {
    return parseFloat(match[1].replace(',', '.'));
  }
  return null;
}

// ── Single Product Card ────────────────────────────────────────────────
function ProductCard({ product, index, onUpdate, onRemove, onDuplicate, canRemove, bibitList, vendorOptions, bahanCair = [], botolList = [], fullsetList = [] }) {
  const [expanded, setExpanded] = useState(false);

  const set = (k, v) => onUpdate({ ...product, [k]: v });

  // Options for Bottle/Fullset - HANYA item yang mengandung kata 'sample' dari Fullset / Botol gudang
  const bottleOptions = React.useMemo(() => {
    // 1. Fullset items containing 'sample'
    const sampleFullsets = (fullsetList || []).filter(f => {
      const n = `${f.nama || ''} ${f.nama_item || ''} ${f.deskripsi || ''}`.toLowerCase();
      return n.includes('sample');
    });

    // 2. Botol items containing 'sample'
    const sampleBotols = (botolList || []).filter(b => {
      const n = `${b.nama || ''} ${b.nama_item || ''}`.toLowerCase();
      return n.includes('sample');
    });

    const mapItem = (item, table) => {
      const name = item.nama || item.nama_item || table;
      const stok = item.stok || item.stock || 0;
      const vendor = item.vendor || item.supplier || '';
      return {
        value: `${table.toLowerCase()}_${item.id}`,
        id: item.id,
        table,
        name,
        raw: item,
        vendor,
        stok,
        label: `[${table}] ${name} (Stok: ${stok} pcs)${vendor ? ` • ${vendor}` : ''}`
      };
    };

    return [
      { value: 'none', label: 'Tanpa Kemasan / Botol' },
      { value: 'client', label: 'Botol / Kemasan dari Klien (Tidak potong stok)' },
      ...sampleFullsets.map(f => mapItem(f, 'Fullset')),
      ...sampleBotols.map(b => mapItem(b, 'Botol'))
    ];
  }, [fullsetList, botolList]);

  const currentBottleValue = product.botol_dari_client 
    ? 'client' 
    : product.botol_id 
      ? `${product.botol_table === 'Botol' ? 'botol' : 'fullset'}_${product.botol_id}`
      : 'none';

  const selectedBottleObj = bottleOptions.find(o => o.value === currentBottleValue);

  const handleBottleChange = (val) => {
    if (val === 'none' || !val) {
      onUpdate({ ...product, botol_id: '', botol_table: 'Fullset', botol_name: '', botol_dari_client: false });
    } else if (val === 'client') {
      onUpdate({ ...product, botol_id: '', botol_table: 'Fullset', botol_name: 'Botol dari Klien', botol_dari_client: true });
    } else {
      const found = bottleOptions.find(o => o.value === val);
      if (found) {
        const detectedMl = extractVolumeFromItem(found.raw);
        let newUkuran = product.ukuran_ml;
        let newRacikan = Array.isArray(product.racikan) ? product.racikan : [];

        // Otomatis sinkronkan volume sample (ml) dengan volume botol yang dipilih
        if (detectedMl && detectedMl > 0) {
          newUkuran = detectedMl;
          newRacikan = newRacikan.map(r => ({
            ...r,
            volume_ml: parseFloat(((parseDecimal(r.persentase) / 100) * detectedMl).toFixed(2))
          }));
        }

        onUpdate({
          ...product,
          ukuran_ml: newUkuran,
          racikan: newRacikan,
          botol_id: found.id,
          botol_table: found.table,
          botol_name: found.name,
          botol_dari_client: false
        });
      }
    }
  };

  const racikanArray = Array.isArray(product.racikan) ? product.racikan : [];
  const totalPersen = racikanArray.reduce((s, r) => s + parseDecimal(r.persentase), 0);
  const isValid = Math.abs(totalPersen - 100) < 0.01;

  const addBahan = () => set('racikan', [...racikanArray, emptyBahan()]);
  const removeBahan = (i) => set('racikan', racikanArray.filter((_, idx) => idx !== i));
  const updateBahan = (i, k, v) => {
    let newKodeProduk = product.kode_produk;
    const updated = racikanArray.map((r, idx) => {
      if (idx !== i) return r;
      const next = { ...r, [k]: v };
      if (k === 'kategori_bahan') {
        if (v === 'Alkohol') { next.nama_bahan = 'Superfine'; next.vendor = ''; }
        else if (v === 'Aqua Des') { next.nama_bahan = 'Aqua Des'; next.vendor = ''; }
        else if (v === 'DPG') { next.nama_bahan = 'Dipropylene Glycol'; next.vendor = ''; }
        else if (v === 'Peg') { next.nama_bahan = 'PEG 40'; next.vendor = ''; }
        else if (v === 'Sustain') { next.nama_bahan = 'Sustain'; next.vendor = ''; }
        else if (v === 'Quidex') { next.nama_bahan = 'Quidex'; next.vendor = ''; }
        else if (v === 'Castor Oil') { next.nama_bahan = 'Castor Oil'; next.vendor = ''; }
        else if (v === 'Lainnya') { next.nama_bahan = ''; next.vendor = ''; }
        else if (v === 'Bibit') { next.nama_bahan = ''; next.vendor = ''; }
      }
      if (k === 'persentase') {
        const numericV = parseDecimal(v);
        next.volume_ml = parseFloat(((numericV / 100) * (product.ukuran_ml || 0)).toFixed(2));
      }
      if (k === 'nama_bahan' && next.kategori_bahan === 'Bibit' && !next.dari_client) {
        if (!newKodeProduk && v) {
          const parts = v.split(' - ');
          if (parts.length > 1) {
            newKodeProduk = parts[0].trim();
          }
        }
      }
      return next;
    });

    if (newKodeProduk !== product.kode_produk) {
      onUpdate({ ...product, racikan: updated, kode_produk: newKodeProduk });
    } else {
      set('racikan', updated);
    }
  };

  const handleUkuran = (ml) => {
    const updatedRacikan = racikanArray.map(r => ({
      ...r,
      volume_ml: parseFloat(((parseDecimal(r.persentase) / 100) * (parseFloat(ml) || 0)).toFixed(2))
    }));
    onUpdate({ ...product, ukuran_ml: ml, racikan: updatedRacikan });
  };

  return (
    <div className="bg-card rounded-2xl border border-border shadow-xs relative">
      {/* Product Header */}
      <div className="flex items-center gap-3 p-3 bg-muted/40 border-b border-border/60 rounded-t-2xl">
        <div className="w-5 h-5 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center text-xs font-bold shrink-0">
          {index + 1}
        </div>
        <div className="flex-1">
          <Input
            value={product.nama_produk}
            onChange={e => set('nama_produk', e.target.value)}
            placeholder="Contoh: Sun Zest Serum"
            className="h-8 text-xs font-medium bg-transparent border-transparent hover:border-border focus:border-border"
          />
        </div>
        <button type="button" onClick={onDuplicate} className="text-slate-600 hover:text-slate-900 text-xs font-medium transition-colors flex items-center gap-1">
          <Copy className="w-3.5 h-3.5 text-slate-600" /> Duplikat
        </button>
        {canRemove && (
          <button type="button" onClick={onRemove} className="text-slate-500 hover:text-slate-800 text-xs font-medium transition-colors flex items-center gap-1">
            <Trash2 className="w-3.5 h-3.5 text-slate-500" /> Hapus
          </button>
        )}
        <button type="button" onClick={() => setExpanded(e => !e)} className="text-muted-foreground hover:text-foreground transition-colors">
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </button>
      </div>

      {/* Product Body */}
      {expanded && (
        <div className="p-4 space-y-4">
          {/* Kode + Ukuran + Label */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Ukuran Sample (ml)</label>
              <Input
                type="number" min="0" step="0.1"
                value={product.ukuran_ml}
                onChange={e => handleUkuran(e.target.value)}
                placeholder="Misal: 10"
                className="h-9"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Label Sample</label>
              <div className="flex flex-wrap gap-1.5">
                {LABEL_OPTIONS.map(l => (
                  <button key={l} type="button" onClick={() => set('label', l)}
                    className={`w-8 h-8 rounded-lg border text-sm font-bold transition-all ${product.label === l ? 'bg-primary text-primary-foreground border-primary' : 'border-border text-muted-foreground hover:border-primary/50'}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Pilihan Botol / Kemasan Sample */}
          <div className="bg-slate-50/70 p-3 rounded-xl border border-slate-200/80 space-y-1.5 relative z-[60]">
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-slate-800">
                Pilih Botol / Kemasan Sample
              </label>
              {selectedBottleObj && selectedBottleObj.stok !== undefined && !product.botol_dari_client && (
                <span className="text-[11px] font-medium text-slate-700 bg-white px-2 py-0.5 rounded border border-slate-200">
                  Sisa Stok: {selectedBottleObj.stok} pcs
                </span>
              )}
            </div>
            <SearchableCombobox
              value={currentBottleValue}
              onValueChange={handleBottleChange}
              options={bottleOptions}
              placeholder="Pilih botol/fullset sample dari gudang..."
              allowCustom={false}
              className="bg-white"
            />
            {product.botol_dari_client && (
              <p className="text-[11px] text-slate-500 italic">
                * Botol disediakan langsung oleh klien (stok gudang tidak akan terpotong).
              </p>
            )}
            {selectedBottleObj && selectedBottleObj.value !== 'none' && selectedBottleObj.value !== 'client' && (
              <p className="text-[11px] font-medium text-slate-700 flex items-center gap-1 mt-1 bg-white p-1.5 rounded-lg border border-slate-200">
                <span>• Ukuran sample otomatis diatur ke <b>{product.ukuran_ml} ml</b> mengikuti botol ({selectedBottleObj.name}).</span>
              </p>
            )}
          </div>

          {/* Racikan */}
          <div className="relative z-[20]">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <FlaskConical className="w-4 h-4 text-slate-700" />
                <h4 className="text-sm font-semibold text-slate-800">Racikan Bahan</h4>
              </div>
              <div className="flex items-center gap-2">
                {racikanArray.length > 0 && (
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
                    {totalPersen.toFixed(1)}% / 100%
                  </span>
                )}
                <Button type="button" size="sm" variant="outline" onClick={addBahan} className="h-7 text-xs gap-1 text-slate-700 border-slate-200">
                  <Plus className="w-3 h-3 text-slate-600" /> Tambah Bahan
                </Button>
              </div>
            </div>

            {racikanArray.length === 0 && (
              <div className="text-center py-4 border border-dashed border-border rounded-xl text-muted-foreground text-sm">
                Belum ada bahan. Klik "Tambah Bahan" untuk mulai menyusun racikan.
              </div>
            )}

            <div className="space-y-3">
              {racikanArray.map((row, i) => {
                const isBibit = row.kategori_bahan === 'Bibit';
                const matchedBibit = isBibit && !row.dari_client
                  ? bibitList.find(b => b.nama_bibit?.toLowerCase() === row.nama_bahan?.toLowerCase() && (!row.vendor || b.vendor?.toLowerCase() === row.vendor?.toLowerCase()))
                  : null;

                const matchedBahanCair = !isBibit && !row.dari_client
                  ? bahanCair.find(b => (b.nama || b.nama_bahan || '').toLowerCase() === (row.nama_bahan || '').toLowerCase())
                  : null;

                const stockVendor = matchedBahanCair ? (matchedBahanCair.vendor || matchedBahanCair.supplier) : (matchedBibit ? (matchedBibit.vendor || row.vendor) : null);

                const categoryBahanOptions = bahanCair
                  .filter(b => {
                    const bName = (b.nama || b.nama_bahan || '').toLowerCase();
                    const bKat = (b.kategori || '').toLowerCase();
                    if (row.kategori_bahan === 'Alkohol') return bName.includes('alkohol') || bName.includes('superfine') || bKat.includes('alkohol');
                    if (row.kategori_bahan === 'Aqua Des') return bName.includes('aqua') || bName.includes('air') || bKat.includes('aqua');
                    if (row.kategori_bahan === 'DPG') return bName.includes('dpg') || bName.includes('dipropylene') || bKat.includes('dpg');
                    if (row.kategori_bahan === 'Peg') return bName.includes('peg') || bKat.includes('peg');
                    if (row.kategori_bahan === 'Sustain') return bName.includes('sustain') || bKat.includes('sustain');
                    if (row.kategori_bahan === 'Quidex') return bName.includes('quidex') || bName.includes('guidex') || bKat.includes('quidex');
                    if (row.kategori_bahan === 'Castor Oil') return bName.includes('castor') || bKat.includes('castor');
                    return true;
                  })
                  .map(b => {
                    const bName = b.nama || b.nama_bahan;
                    const vName = b.vendor || b.supplier;
                    return {
                      value: bName,
                      label: `${bName} ${vName ? `[${vName}]` : ''} • Stok: ${formatDecimalNumber(b.stok || 0)} ${b.satuan || 'ml'}`
                    };
                  });

                return (
                  <div key={i} className="bg-white rounded-xl p-3 space-y-2 border border-border/50 relative" style={{ zIndex: 50 - i }}>
                    {/* Row 1: kategori + dari client */}
                    <div className="flex gap-2 flex-wrap items-center">
                      {KATEGORI_BAHAN.map(k => (
                        <button key={k} type="button" onClick={() => updateBahan(i, 'kategori_bahan', k)}
                          className={`px-2.5 py-1 rounded-lg border text-xs font-medium transition-all ${row.kategori_bahan === k ? 'bg-slate-800 text-white border-slate-800' : 'border-slate-200 text-slate-600 hover:border-slate-400 bg-white'}`}>
                          {k}
                        </button>
                      ))}
                      <label className="ml-auto flex items-center gap-1.5 text-xs cursor-pointer select-none">
                        <input 
                          type="checkbox" 
                          checked={row.dari_client || false} 
                          onChange={e => updateBahan(i, 'dari_client', e.target.checked)}
                          className="rounded border-slate-300 text-slate-700 focus:ring-slate-500"
                        />
                        <span className="text-slate-600 font-medium">Dari Client</span>
                      </label>
                      <Button type="button" variant="ghost" size="icon" className="w-7 h-7 text-slate-400 hover:text-slate-700" onClick={() => removeBahan(i)}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                    {/* Row 2: vendor (khusus bibit) + nama bahan + persen */}
                    <div className="grid grid-cols-12 gap-2">
                      {row.kategori_bahan === 'Bibit' && !row.dari_client && (
                        <div className="col-span-3 relative z-[60]">
                          <label className="text-[10px] text-muted-foreground block mb-0.5">Vendor *</label>
                          <SearchableCombobox
                            value={row.vendor}
                            onValueChange={v => {
                              const updated = racikanArray.map((r, idx) => {
                                if (idx !== i) return r;
                                return { ...r, vendor: v, nama_bahan: '', grade: '' };
                              });
                              set('racikan', updated);
                            }}
                            options={vendorOptions}
                            placeholder="Pilih vendor *"
                            searchPlaceholder="Ketik untuk cari..."
                            size="sm"
                          />
                        </div>
                      )}
                      {row.kategori_bahan === 'Bibit' && !row.dari_client && row.vendor?.toLowerCase().includes('parfarome') && (
                        <div className="col-span-12">
                          <label className="text-[10px] text-slate-700 font-semibold block mb-0.5">Grade Parfarome * (wajib pilih)</label>
                          <div className="flex gap-1.5">
                            {PARFAROME_GRADE.map(g => (
                              <button key={g} type="button" onClick={() => updateBahan(i, 'grade', g)}
                                className={`px-3 py-1 rounded-lg border text-xs font-semibold transition-all ${row.grade === g ? 'bg-slate-800 text-white border-slate-800' : 'border-slate-200 text-slate-700 hover:border-slate-400 bg-white'}`}>
                                {g}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className={row.kategori_bahan === 'Bibit' && !row.dari_client ? 'col-span-5 relative z-[50]' : 'col-span-8 relative z-[50]'}>
                        <label className="text-[10px] text-muted-foreground block mb-0.5">Nama Bahan {row.dari_client ? '(Opsional)' : '*'}</label>
                        {row.kategori_bahan === 'Bibit' && !row.dari_client ? (
                          <SearchableCombobox
                            value={row.nama_bahan}
                            onValueChange={v => updateBahan(i, 'nama_bahan', v)}
                            options={bibitList
                              .filter(b => b.status_ketersediaan !== 'Tidak Tersedia' && b.vendor === row.vendor && (!row.grade || !row.vendor.toLowerCase().includes('parfarome') || b.grade === row.grade))
                              .sort((a, b) => (a.nama_bibit || '').localeCompare(b.nama_bibit || ''))
                              .map(b => ({ value: b.nama_bibit, label: b.nama_bibit }))
                            }
                            placeholder="Pilih Bibit"
                            searchPlaceholder="Ketik untuk cari..."
                            disabled={!row.vendor}
                            size="sm"
                          />
                        ) : row.dari_client ? (
                          <Input
                            value={row.nama_bahan}
                            onChange={e => updateBahan(i, 'nama_bahan', e.target.value)}
                            placeholder="Default: Bibit dari Client"
                            className="h-8 text-xs"
                          />
                        ) : (
                          <SearchableCombobox
                            value={row.nama_bahan}
                            onValueChange={v => updateBahan(i, 'nama_bahan', v)}
                            options={categoryBahanOptions}
                            placeholder={`Pilih ${row.kategori_bahan}...`}
                            searchPlaceholder="Cari dari stok gudang..."
                            allowCustom={true}
                            size="sm"
                          />
                        )}
                      </div>
                      <div className="col-span-2">
                        <label className="text-[10px] text-muted-foreground block mb-0.5">% *</label>
                        <Input
                          type="text"
                          inputMode="decimal"
                          value={row.persentase === 0 ? '' : row.persentase}
                          onChange={e => updateBahan(i, 'persentase', e.target.value)}
                          className="h-8 text-xs font-black text-center"
                          placeholder="0"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="text-[10px] text-muted-foreground block mb-0.5">Volume</label>
                        {(() => {
                          const isLiter = needsLiterConversion('', row.kategori_bahan);
                          const volGram = isLiter && row.volume_ml > 0 ? row.volume_ml * 0.8 : null;
                          return (
                            <div className="h-8 flex items-center px-2 bg-muted rounded-md text-xs text-muted-foreground font-medium">
                              {volGram !== null
                                ? <span><span className="text-foreground font-semibold">{parseFloat(volGram.toFixed(2))}g</span><span className="font-normal">/{row.volume_ml}ml</span></span>
                                : `${row.volume_ml || 0} ml`}
                            </div>
                          );
                        })()}
                      </div>
                    </div>

                    {/* Stock & Vendor Badges */}
                    {matchedBahanCair && !row.dari_client && (
                      <div className="flex items-center gap-1.5 text-[10px] text-slate-700 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1">
                        <CheckCircle2 className="w-3.5 h-3.5 text-slate-600 shrink-0" />
                        <span className="font-bold">Item Inventori Gudang Terhubung</span>
                        {stockVendor && <span className="text-slate-600 font-semibold">• Vendor: {stockVendor}</span>}
                        <span className="text-slate-500">• Sisa Stok: {formatDecimalNumber(matchedBahanCair.stok || 0)} {matchedBahanCair.satuan || 'ml'}</span>
                      </div>
                    )}
                    {matchedBibit && !row.dari_client && (
                      <div className="flex items-center gap-1.5 text-[10px] text-slate-700 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1">
                        <CheckCircle2 className="w-3.5 h-3.5 text-slate-600 shrink-0" />
                        <span className="font-bold">Item Database Bibit Terhubung</span>
                        <span className="text-slate-600 font-semibold">• Vendor: {row.vendor || matchedBibit.vendor}</span>
                        {matchedBibit.grade && <span className="text-slate-500">• Grade: {matchedBibit.grade}</span>}
                      </div>
                    )}
                    {row.dari_client && (
                      <div className="flex items-center gap-1.5 text-[10px] text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1">
                        <AlertCircle className="w-3 h-3 text-slate-500 shrink-0" />
                        <span className="font-medium">Item ini dikirim oleh client (tidak memotong stok gudang)</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {racikanArray.length > 0 && !isValid && (
              <div className="mt-2 flex items-center gap-2 text-xs rounded-lg px-3 py-2 bg-slate-50 text-slate-700 border border-slate-200">
                <AlertCircle className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                {totalPersen > 100 ? `Melebihi 100% (${totalPersen.toFixed(1)}%)` : `Kurang ${(100 - totalPersen).toFixed(1)}% lagi`}
              </div>
            )}
            {racikanArray.length > 0 && isValid && (
              <div className="mt-2 flex items-center gap-2 text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                <CheckCircle2 className="w-3.5 h-3.5 text-slate-600" /> Racikan valid 100%
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main SampleForm ────────────────────────────────────────────────────
export default function SampleForm({ item, onClose, onSaved, focusProductIndex }) {
  const qc = useQueryClient();
  const isEdit = !!item?.id;

  const { member } = useSession();
  const { sapaan: asaNickname } = useAsaPersonality();

  // Brand-level state
  const { session, user } = useSession() || {};
  const currentUserName = session?.user?.name || user?.nama || '';

  const [brandForm, setBrandForm] = useState({
    nama_brand: item?.nama_brand || '',
    kategori: item?.kategori || 'SPL',
    pic_brand: item?.pic_brand || '',
    pic_pembuat: item?.pic_pembuat || currentUserName || '',
    catatan: item?.catatan || '',
    source_invoice_id: item?.source_invoice_id || null,
    source_invoice_number: item?.source_invoice_number || null,
    source_invoice_client: item?.source_invoice_client || null,
    created_by_role: item?.created_by_role || 'manual',
    alamat_pengiriman: item?.alamat_pengiriman || null,
  });

  // Products state
  const [products, setProducts] = useState(() => {
    const allProducts = legacyToProducts(item);
    if (focusProductIndex !== undefined && focusProductIndex >= 0 && focusProductIndex < allProducts.length) {
      return [allProducts[focusProductIndex]];
    }
    return allProducts;
  });

  const { data: bibitList = [] } = useQuery({
    queryKey: ['bibit-sample'],
    queryFn: () => base44.entities.BibitSample.list()
  });

  const vendorOptions = [...new Set(bibitList
    .filter(b => b.status_ketersediaan !== 'Tidak Tersedia')
    .map(b => b.vendor)
    .filter(Boolean))].sort();

  const { data: clientAccounts = [] } = useQuery({ queryKey: ['client-accounts'], queryFn: () => base44.entities.ClientAccount.list() });
  const { data: clientPOs = [] } = useQuery({ queryKey: ['client-po'], queryFn: () => base44.entities.ClientPO.list() });
  
  const allKnownBrands = [...new Set([
    ...clientAccounts.filter(c => c.status !== 'deleted').map(c => c.brand_name),
    ...clientPOs.map(po => po.customerName ? po.customerName.split('/')[0].trim() : '')
  ].filter(Boolean).filter(name => {
    const n = name.toLowerCase();
    return !n.includes('developer') && !n.includes('dev client') && !n.includes('test') && !n.includes('sample data');
  }))].sort();

  const matchedClient = clientAccounts.find(c => c.brand_name?.toLowerCase() === brandForm.nama_brand?.toLowerCase());
  const availableAddresses = matchedClient?.addresses || (matchedClient?.location && matchedClient.location !== '-' ? [{ id: 'loc-1', penerima: matchedClient.pic_name || matchedClient.pic || matchedClient.owner_name, telepon: matchedClient.phone !== '-' ? matchedClient.phone : '', alamat_lengkap: matchedClient.location, kode_pos: '' }] : []);

  const getInitialAddressOption = () => {
    if (item?.alamat_pengiriman) {
      // Check if it matches an existing address ID
      const matched = availableAddresses.find(a => a.id === item.alamat_pengiriman.id);
      if (matched) return matched.id;
      // Otherwise it was a custom manual input
      return 'custom';
    }
    return 'empty';
  };

  const [addressOption, setAddressOption] = useState(getInitialAddressOption());
  const [newAddress, setNewAddress] = useState(
    item?.alamat_pengiriman && getInitialAddressOption() === 'custom' 
      ? item.alamat_pengiriman 
      : { nama_alamat: '', penerima: '', telepon: '', alamat_lengkap: '', kode_pos: '' }
  );
  const [showSaveAddressPrompt, setShowSaveAddressPrompt] = useState(false);
  const [savedPayload, setSavedPayload] = useState(null);
  
  // States for Stock Deduction
  const [asaDialog, setAsaDialog] = useState(null);
  
  // Fetch inventories for name resolution and default pelarut
  const { data: bahanCair = [] } = useQuery({ queryKey: ['bahan-cair'], queryFn: () => base44.entities.BahanCair.list() });
  const { data: fullsetList = [] } = useQuery({ queryKey: ['fullset'], queryFn: () => base44.entities.Fullset.list() });
  const { data: botolList = [] } = useQuery({ queryKey: ['botol'], queryFn: () => base44.entities.Botol.list() });

  // Auto-select first address if available and option is auto
  useEffect(() => {
    if (addressOption === 'auto' && availableAddresses.length > 0) {
      setAddressOption(availableAddresses[0].id);
    }
  }, [availableAddresses, addressOption]);

  const setBrand = (k, v) => setBrandForm(p => ({ ...p, [k]: v }));

  const updateProduct = (idx, updated) => {
    setProducts(prev => prev.map((p, i) => i === idx ? updated : p));
  };

  const removeProduct = (idx) => {
    setProducts(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);
  };

  const addProduct = () => {
    setProducts(prev => [...prev, emptyProduct(prev.length)]);
  };

  const duplicateProduct = (idx) => {
    const source = products[idx];
    
    setProducts(prev => {
      // Cari label yang belum terpakai untuk nama_produk yang SAMA
      const sameNameProducts = prev.filter(p => p.nama_produk === source.nama_produk);
      const usedLabels = sameNameProducts.map(p => p.label);
      const nextLabel = LABEL_OPTIONS.find(l => !usedLabels.includes(l)) || LABEL_OPTIONS[sameNameProducts.length % LABEL_OPTIONS.length];

      const newProduct = {
        ...JSON.parse(JSON.stringify(source)),
        _id: Date.now() + Math.random(),
        nama_produk: source.nama_produk, // Biarkan sama persis
        label: nextLabel,
        status: 'pending',
        catatan_review: '',
      };

      const newProducts = [...prev];
      newProducts.splice(idx + 1, 0, newProduct); // Insert exactly after idx
      return newProducts;
    });
  };

  // Check if all products have valid racikan (or no racikan)
  const allProductsValid = products.every(p => {
    const arr = Array.isArray(p.racikan) ? p.racikan : [];
    if (arr.length === 0) return true;
    const total = arr.reduce((s, r) => s + parseDecimal(r.persentase), 0);
    return Math.abs(total - 100) < 0.01;
  });

  const saveMut = useMutation({
    mutationFn: (data) =>
      isEdit
        ? base44.entities.SampleRacikan.update(item.id, data)
        : base44.entities.SampleRacikan.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sample-racikan'] });
      onSaved?.();
    },
  });



  const handleSubmit = (e) => {
    e.preventDefault();
    if (!allProductsValid) return;
    if (products.some(p => !p.nama_produk?.trim())) return;

    // Normalize racikan in each product
    const normalizedProducts = products.map(p => ({
      ...p,
      racikan: (Array.isArray(p.racikan) ? p.racikan : []).map(r => ({
        ...r,
        persentase: parseFloat(String(r.persentase).replace(',', '.')) || 0,
        volume_ml: parseFloat(r.volume_ml) || 0,
      }))
    }));

    let finalAlamat = null;
    if (addressOption === 'custom') {
      if (newAddress.penerima?.trim() || newAddress.alamat_lengkap?.trim()) {
        finalAlamat = newAddress;
      }
    } else if (addressOption !== 'empty' && addressOption !== 'auto') {
      finalAlamat = availableAddresses.find(a => a.id === addressOption);
    }

    let finalProducts = normalizedProducts;
    if (focusProductIndex !== undefined && focusProductIndex >= 0 && item?.products) {
      const originalProducts = legacyToProducts(item);
      finalProducts = [
        ...originalProducts.slice(0, focusProductIndex),
        ...normalizedProducts,
        ...originalProducts.slice(focusProductIndex + 1)
      ];
    }

    const payload = {
      ...brandForm,
      alamat_pengiriman: finalAlamat,
      products: finalProducts,
      status: 'pending',
      // Legacy compat fields (first product)
      nama_produk: normalizedProducts[0]?.nama_produk || '',
      ukuran_ml: normalizedProducts[0]?.ukuran_ml || '',
      label: normalizedProducts[0]?.label || 'A',
      racikan: normalizedProducts[0]?.racikan || [],
    };

    // Calculate Stock Deductions for Bottles (Fullset / Botol)
    const botolMap = {};
    normalizedProducts.forEach(p => {
      const qty = p.invoice_qty || 1;
      if (p.botol_id && !p.botol_dari_client) {
        const table = p.botol_table || 'Fullset';
        const key = `${table}_${p.botol_id}`;
        if (!botolMap[key]) {
          const bList = table === 'Botol' ? botolList : fullsetList;
          const found = bList.find(x => String(x.id) === String(p.botol_id));
          botolMap[key] = {
            id: p.botol_id,
            table,
            name: found?.nama || found?.nama_item || p.botol_name || 'Botol Fullset',
            vendor: found?.vendor || found?.supplier || '',
            stok: found?.stok || found?.stock || 0,
            qty: 0
          };
        }
        botolMap[key].qty += qty;
      }
    });

    const botolsToDeduct = Object.values(botolMap);

    // Calculate Solvent / Alkohol / Superfine Volume
    let totalPelarutVolume = 0;
    let pelarutName = 'Superfine / Alkohol';
    let targetPelarutId = null;

    normalizedProducts.forEach(p => {
      const qty = p.invoice_qty || 1;
      if (p.pelarut_id) {
        targetPelarutId = p.pelarut_id;
      }
      p.racikan.forEach(r => {
        if (r.dari_client) return;
        const isAlko = r.kategori_bahan === 'Alkohol' || 
                       (r.nama_bahan || '').toLowerCase().includes('alkohol') || 
                       (r.nama_bahan || '').toLowerCase().includes('superfine');
        if (isAlko) {
           totalPelarutVolume += ((r.volume_ml || 0) * qty);
        }
      });
    });

    let pelarutVendor = '';
    let pelarutStok = 0;
    let pelarutSatuan = 'ml';
    if (!targetPelarutId && totalPelarutVolume > 0) {
      // Find default superfine or alkohol
      const defaultAlko = bahanCair.find(b => (b.nama || b.nama_bahan || '').toLowerCase().includes('superfine') || (b.nama || b.nama_bahan || '').toLowerCase().includes('alkohol'));
      if (defaultAlko) {
        targetPelarutId = defaultAlko.id;
        pelarutName = defaultAlko.nama || defaultAlko.nama_bahan;
        pelarutVendor = defaultAlko.vendor || defaultAlko.supplier || '';
        pelarutStok = defaultAlko.stok || defaultAlko.stock || 0;
        pelarutSatuan = defaultAlko.satuan || 'ml';
      }
    } else if (targetPelarutId) {
      const bCair = bahanCair.find(b => b.id === targetPelarutId);
      if (bCair) {
        pelarutName = bCair.nama || bCair.nama_bahan;
        pelarutVendor = bCair.vendor || bCair.supplier || '';
        pelarutStok = bCair.stok || bCair.stock || 0;
        pelarutSatuan = bCair.satuan || 'ml';
      }
    }

    // Jika ini adalah pengajuan sample baru dari invoice (oleh marketing), lewati pemotongan stok karena belum tahap produksi/racikan
    const isNewInvoiceSubmission = !isEdit && (item?.source_invoice_id || item?.source_invoice_number || item?.created_by_role === 'marketing' || brandForm.source_invoice_number);
    if (isNewInvoiceSubmission) {
      proceedSave(payload);
      return;
    }

    // Jika sample sudah pernah dikonfirmasi/dipotong stoknya (status sudah dikerjakan/selanjutnya), jangan potong ulang
    const isAlreadyDeducted = isEdit && (item?.stok_terpotong || item?.status === 'dikerjakan' || item?.status === 'menunggu_dikirim' || item?.status === 'dikirim' || item?.status === 'acc');
    if (isAlreadyDeducted) {
      proceedSave({
        ...payload,
        status: item?.status || 'dikerjakan',
        stok_terpotong: true,
      });
      return;
    }

    // Hanya tampilkan dialog pemotongan stok JIKA ada botol atau pelarut (alkohol/superfine) yang benar-benar ada di inventori
    const hasPelarutInInventory = !!targetPelarutId && totalPelarutVolume > 0;
    if (botolsToDeduct.length > 0 || hasPelarutInInventory) {
      setAsaDialog({
        show: true,
        payload,
        deductions: {
          botols: botolsToDeduct,
          pelarut: hasPelarutInInventory ? { vol: totalPelarutVolume, id: targetPelarutId, name: pelarutName, vendor: pelarutVendor, stok: pelarutStok, satuan: pelarutSatuan } : null
        }
      });
      return;
    }

    proceedSave(payload);
  };

  const proceedSave = (payload) => {
    if (addressOption === 'custom' && payload.alamat_pengiriman) {
       setSavedPayload(payload);
       setShowSaveAddressPrompt(true);
    } else {
       saveMut.mutate(payload);
    }
  };

  const handleConfirmSaveAddress = async (saveToClient) => {
    if (saveToClient) {
       let updatedAddresses = [];
       if (!matchedClient) {
         try {
           const newClientData = {
             brand_name: brandForm.nama_brand,
             status: 'unverified',
             addresses: [{ id: `addr-${Date.now()}`, ...newAddress }],
             pic_name: brandForm.pic_brand || '',
             created_date: new Date().toISOString()
           };
           await base44.entities.ClientAccount.create(newClientData);
           updatedAddresses = newClientData.addresses;
         } catch(e) {
           toast.error('Gagal membuat profil klien baru');
           return;
         }
       } else {
         updatedAddresses = [...(matchedClient.addresses || []), { id: `addr-${Date.now()}`, ...newAddress }];
         try {
           await base44.entities.ClientAccount.update(matchedClient.id, { addresses: updatedAddresses });
         } catch (e) {
           toast.error('Gagal menyimpan alamat ke data klien');
           return;
         }
       }

       qc.invalidateQueries({ queryKey: ['client-accounts'] });
       toast.success('Alamat berhasil ditambahkan ke Data Klien');
       savedPayload.alamat_pengiriman = { id: updatedAddresses[updatedAddresses.length-1].id, ...newAddress };
    }
    
    // We should not call onSaved here if we are about to mutate saveMut, 
    // because onSaved usually unmounts this modal component! 
    // Let saveMut.onSuccess handle onSaved.
    saveMut.mutate(savedPayload);
    setShowSaveAddressPrompt(false);
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/40 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-3xl my-8">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h3 className="text-lg font-semibold">{isEdit ? 'Edit' : 'Tambah'} Sample</h3>
          <Button variant="ghost" size="icon" onClick={onClose}><X className="w-4 h-4" /></Button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* INVOICE & MARKETING REQUEST BANNER */}
          {(item?.source_invoice_number || brandForm.source_invoice_number || brandForm.catatan || item?.catatan) && (
            <div className="bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50/60 border border-amber-200/90 rounded-2xl p-4 text-xs space-y-2.5 shadow-xs">
              <div className="flex items-center justify-between">
                <span className="font-extrabold text-amber-900 flex items-center gap-2 text-sm">
                  <FileText className="w-4 h-4 text-amber-700" />
                  {item?.source_invoice_number || brandForm.source_invoice_number 
                    ? `Permintaan Sample dari Invoice #${item?.source_invoice_number || brandForm.source_invoice_number}`
                    : 'Pengajuan Permintaan Sample (Marketing)'}
                </span>
                <Badge className="bg-amber-100 text-amber-900 border-amber-300 font-extrabold text-[10px] px-2 py-0.5 shadow-2xs gap-1 inline-flex items-center">
                  {item?.source_invoice_number || brandForm.source_invoice_number ? (
                    <>
                      <FileText className="w-3 h-3 text-amber-700" />
                      <span>Via Invoice Client</span>
                    </>
                  ) : (
                    <>
                      <PenTool className="w-3 h-3 text-amber-700" />
                      <span>Input Manual</span>
                    </>
                  )}
                </Badge>
              </div>

              {(brandForm.catatan || item?.catatan) && (
                <div className="bg-white/90 p-3 rounded-xl border border-amber-200 text-slate-800 shadow-2xs">
                  <span className="font-black text-amber-900 block text-xs mb-1 flex items-center gap-1.5">
                    <Lightbulb className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                    <span>Catatan / Instruksi dari Peminta (Marketing):</span>
                  </span>
                  <p className="italic text-slate-700 font-medium text-xs bg-amber-50/50 p-2 rounded-lg border border-amber-100/80">
                    "{brandForm.catatan || item?.catatan}"
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Brand Info */}
          <div className="space-y-3 p-4 bg-muted/30 rounded-xl border border-border">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Info Brand</h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="z-[70]">
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Nama Brand *</label>
                <SearchableCombobox
                  value={brandForm.nama_brand}
                  onValueChange={v => setBrand('nama_brand', v)}
                  options={allKnownBrands.map(b => ({ value: b, label: b }))}
                  placeholder="Pilih atau ketik brand baru..."
                  searchPlaceholder="Cari brand..."
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Kategori</label>
                <div className="flex flex-wrap gap-1.5">
                  {KATEGORI_SAMPLE.map(k => (
                    <button key={k} type="button" onClick={() => setBrand('kategori', k)}
                      className={`px-2.5 py-1 rounded-lg border text-xs font-medium transition-all ${brandForm.kategori === k ? 'bg-accent text-accent-foreground border-accent' : 'border-border text-muted-foreground hover:border-accent/50'}`}>
                      {k}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Catatan Khusus / Instruksi Pembuat (Marketing)</label>
              <Input
                type="text"
                value={brandForm.catatan}
                onChange={e => setBrand('catatan', e.target.value)}
                placeholder="Misal: Buat 2 masing-masing sample dingin dan sedikit oily..."
                className="h-9 text-xs"
              />
            </div>
          </div>

          {/* Products */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                Produk / Varian ({products.length})
              </h4>
            </div>

            {products.map((product, idx) => (
              <ProductCard
                key={product._id}
                product={product}
                index={idx}
                onUpdate={(updated) => updateProduct(idx, updated)}
                onRemove={() => removeProduct(idx)}
                onDuplicate={() => duplicateProduct(idx)}
                canRemove={products.length > 1}
                bibitList={bibitList}
                vendorOptions={vendorOptions}
                bahanCair={bahanCair}
                botolList={botolList}
                fullsetList={fullsetList}
              />
            ))}

            {focusProductIndex === undefined && (
              <Button type="button" variant="outline" onClick={addProduct}
                className="w-full h-9 text-sm gap-2 border-dashed border-amber-300 text-amber-700 hover:bg-amber-50">
                <Plus className="w-4 h-4" /> Tambah Produk / Varian
              </Button>
            )}
          </div>

          {/* PIC */}
          <div className="grid grid-cols-2 gap-3">
            <PicSelector
              label="PIC Brand"
              value={brandForm.pic_brand}
              onChange={v => setBrand('pic_brand', v)}
              requiredPageAccessKey="sample_racikan"
              requiredPicRole="brand"
            />
            <PicSelector
              label="PIC Pembuat Sample"
              value={brandForm.pic_pembuat}
              onChange={v => setBrand('pic_pembuat', v)}
              requiredPageAccessKey="sample_racikan"
              requiredPicRole="pembuat"
            />
          </div>

          <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-3">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Alamat Pengiriman</h4>
            <div className="space-y-2">
              <SearchableCombobox
                value={addressOption}
                onValueChange={v => setAddressOption(v)}
                options={[
                  { value: 'empty', label: 'Kosongkan (Opsional)' },
                  { value: 'custom', label: '+ Input Manual Alamat Baru' },
                  ...availableAddresses.map(a => {
                    const recipient = a.penerima?.trim();
                    const title = a.nama_alamat || (a.alamat_lengkap ? a.alamat_lengkap.substring(0, 35) + '...' : 'Alamat');
                    return { 
                      value: a.id, 
                      label: recipient ? `${title} (${recipient})` : title
                    };
                  })
                ]}
                allowCustom={false}
                placeholder="Pilih Alamat..."
              />
              {addressOption === 'custom' ? (
                <div className="space-y-2 mt-2 pt-2 border-t border-slate-200">
                   <div className="grid grid-cols-2 gap-2">
                     <Input placeholder="Nama Penerima" value={newAddress.penerima} onChange={e => setNewAddress({...newAddress, penerima: e.target.value})} className="h-9 text-sm" />
                     <Input placeholder="No Telepon" value={newAddress.telepon} onChange={e => setNewAddress({...newAddress, telepon: e.target.value})} className="h-9 text-sm" />
                   </div>
                   <textarea placeholder="Alamat Lengkap" value={newAddress.alamat_lengkap} onChange={e => setNewAddress({...newAddress, alamat_lengkap: e.target.value})} className="w-full text-sm rounded-md border-slate-200 px-3 py-2 min-h-[60px]" />
                   <Input placeholder="Kode Pos (Opsional)" value={newAddress.kode_pos} onChange={e => setNewAddress({...newAddress, kode_pos: e.target.value})} className="h-9 text-sm w-1/2" />
                </div>
              ) : (
                addressOption !== 'empty' && availableAddresses.find(a => a.id === addressOption) && (
                  <div className="mt-2 pt-2 border-t border-slate-200">
                    {(() => {
                      const selAddr = availableAddresses.find(a => a.id === addressOption);
                      if (!selAddr) return null;
                      const recipient = selAddr.penerima?.trim();
                      const phone = (selAddr.telepon || selAddr.no_hp || selAddr.phone || '')?.trim();
                      return (
                        <div className="bg-white border border-slate-200 rounded-lg p-3 text-xs text-slate-600">
                          {selAddr.nama_alamat && (
                            <p className="font-bold text-slate-800 mb-0.5">{selAddr.nama_alamat}</p>
                          )}
                          {(recipient || phone) && (
                            <p className="font-medium text-slate-700 mb-1">
                              {recipient} {phone ? `(${phone})` : ''}
                            </p>
                          )}
                          <p>{selAddr.alamat_lengkap || selAddr.location || '-'}</p>
                          {selAddr.kode_pos && <p className="text-[11px] text-slate-500 mt-0.5">Kode Pos: {selAddr.kode_pos}</p>}
                        </div>
                      );
                    })()}
                  </div>
                )
              )}
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Catatan</label>
            <Input value={brandForm.catatan} onChange={e => setBrand('catatan', e.target.value)} placeholder="Catatan tambahan (opsional)" />
          </div>

          <div className="pt-2 flex gap-3">
            <Button type="button" variant="ghost" onClick={onClose} className="flex-1">Batal</Button>
            <Button type="submit" disabled={saveMut.isPending || !brandForm.nama_brand.trim() || products.some(p => !p.nama_produk?.trim()) || !allProductsValid} className="flex-1 bg-amber-600 hover:bg-amber-700 text-white">
              {saveMut.isPending ? 'Menyimpan...' : isEdit ? 'Simpan Perubahan' : `Buat Sample (${products.length} Produk)`}
            </Button>
          </div>
        </form>
      </div>

      {showSaveAddressPrompt && (
        <div className="fixed inset-0 z-[200] bg-black/60 flex items-center justify-center p-4 overflow-y-auto">
           <div className="bg-white rounded-2xl p-6 max-w-sm w-full text-center max-h-[90vh] flex flex-col overflow-hidden my-auto">
             <div className="overflow-y-auto flex-1 pr-1">
               <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-amber-300 shadow-md mx-auto mb-4 bg-amber-50 shrink-0">
                 <img src="/asa-profile.png" alt="ASA" className="w-full h-full object-cover" onError={(e) => { e.target.src = 'https://ui-avatars.com/api/?name=Asa&background=0D8ABC&color=fff'; }} />
               </div>
               <h3 className="text-lg font-bold text-slate-800 mb-2">Pesan dari Asa</h3>
               <p className="text-sm text-slate-600 mb-4">
                 Asa lihat kamu baru aja masukin alamat baru nih untuk brand <strong>{brandForm.nama_brand}</strong>. <br/><br/>
                 Mau Asa simpan sekalian alamat ini ke Data Klien nggak? Biar nanti ke depannya tim Produksi gampang banget pas mau nentuin alamat pengiriman barang finalnya, tinggal milih namanya doang!
               </p>
               <div className="text-left mb-6">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">Kasih Nama Alamat (Misal: Selvi Palembang)</label>
                  <Input 
                    placeholder="Nama Alamat..." 
                    value={newAddress.nama_alamat || ''} 
                    onChange={e => setNewAddress({...newAddress, nama_alamat: e.target.value})} 
                    className="h-9 text-sm border-slate-200"
                  />
               </div>
             </div>
             <div className="flex gap-2 pt-3 border-t border-slate-100 shrink-0">
               <Button variant="outline" className="flex-1 text-slate-600" onClick={() => handleConfirmSaveAddress(false)}>Tidak Usah</Button>
               <Button className="flex-1 bg-[#8C6208] text-white hover:bg-[#6c4b05]" onClick={() => handleConfirmSaveAddress(true)} disabled={!newAddress.nama_alamat?.trim()}>Boleh</Button>
             </div>
           </div>
        </div>
      )}

      {asaDialog?.show && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 bg-slate-900/70 backdrop-blur-xs animate-in fade-in duration-200 overflow-y-auto">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden border border-[#E6D5B8] animate-in zoom-in-95 duration-200 flex flex-col max-h-[90vh] my-auto">
            <div className="bg-[#FCFAF5] p-4 sm:p-5 border-b border-[#E6D5B8]/40 flex gap-4 items-center shrink-0">
              <div className="w-10 h-10 bg-white rounded-full shadow-xs flex items-center justify-center shrink-0 border border-[#E6D5B8]">
                <Sparkles className="w-5 h-5 text-[#8C6208]" />
              </div>
              <div className="min-w-0">
                <h3 className="font-bold text-[#302621] text-base">Konfirmasi Pemotongan Stok</h3>
                <p className="text-[11px] text-slate-500 font-medium">Stok otomatis disesuaikan</p>
              </div>
            </div>
            
            <div className="p-4 sm:p-6 overflow-y-auto flex-1 overscroll-contain">
              <div className="flex flex-col items-center justify-center text-center mb-5 shrink-0">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full overflow-hidden border-4 border-white shadow-lg mb-3 bg-slate-100">
                  <img src="/asa-profile.png" alt="ASA" className="w-full h-full object-cover" onError={(e) => { e.target.src = 'https://ui-avatars.com/api/?name=Asa&background=0D8ABC&color=fff'; }} />
                </div>
                <h3 className="text-base font-bold text-[#302621]">ASA AI</h3>
              </div>
              
              <div className="bg-[#FFFDF9] border border-[#E6D5B8]/60 p-4 rounded-xl shadow-xs mb-4 relative flex items-center justify-center">
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-[#FFFDF9] border-t border-l border-[#E6D5B8]/60 rotate-45"></div>
                <p className="text-xs sm:text-sm text-[#302621] text-center font-medium leading-relaxed relative z-10 w-full">
                  Halo <b>{asaNickname}</b>, untuk memproses pembuatan sample ini, sistem akan memotong stok bahan dan kemasan secara otomatis dari gudang.
                </p>
              </div>
              <p className="text-xs font-semibold text-slate-700 leading-relaxed mb-3">
                Detail stok yang akan dipotong:
              </p>
              
              <div className="space-y-2.5 mb-4 pr-0.5">
                {/* Botols */}
                {asaDialog.deductions.botols && asaDialog.deductions.botols.length > 0 && asaDialog.deductions.botols.map((b, bIdx) => (
                  <div key={`botol-${bIdx}`} className="bg-white border border-slate-200 rounded-xl p-3 flex flex-col gap-1.5 shadow-xs">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-xs sm:text-sm text-slate-800 flex items-center gap-2">
                        <Package className="w-4 h-4 text-slate-600 shrink-0" />
                        {b.qty} pcs {b.name}
                      </span>
                      <span className="text-[10px] font-medium bg-slate-100 text-slate-700 px-2 py-0.5 rounded-md border border-slate-200">
                        {b.table || 'Fullset'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-600 pt-1 border-t border-slate-100 flex-wrap">
                      <span className="inline-flex items-center gap-1 font-medium text-slate-700 bg-slate-50 px-2 py-0.5 rounded border border-slate-200 text-[11px]">
                        <CheckCircle2 className="w-3 h-3 text-slate-500" /> Item Gudang
                      </span>
                      {b.vendor && (
                        <span className="text-[11px] font-medium text-slate-700 bg-slate-50 px-2 py-0.5 rounded border border-slate-200">
                          Vendor: {b.vendor}
                        </span>
                      )}
                      <span className="text-[11px] text-slate-500">
                        Sisa: {b.stok} pcs
                      </span>
                    </div>
                  </div>
                ))}

                {/* Pelarut */}
                {asaDialog.deductions.pelarut?.vol > 0 && (
                  <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-col gap-1.5 shadow-xs">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-xs sm:text-sm text-slate-800 flex items-center gap-2">
                        <Database className="w-4 h-4 text-slate-600 shrink-0" />
                        {formatDecimalNumber(asaDialog.deductions.pelarut.vol)} ml {asaDialog.deductions.pelarut.name}
                      </span>
                      <span className="text-[10px] font-medium bg-slate-100 text-slate-700 px-2 py-0.5 rounded-md border border-slate-200">
                        Bahan Cair / Pelarut
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-600 pt-1 border-t border-slate-100 flex-wrap">
                      <span className="inline-flex items-center gap-1 font-medium text-slate-700 bg-slate-50 px-2 py-0.5 rounded border border-slate-200 text-[11px]">
                        <CheckCircle2 className="w-3 h-3 text-slate-500" /> Item Gudang
                      </span>
                      {asaDialog.deductions.pelarut.vendor && (
                        <span className="text-[11px] font-medium text-slate-700 bg-slate-50 px-2 py-0.5 rounded border border-slate-200">
                          Vendor: {asaDialog.deductions.pelarut.vendor}
                        </span>
                      )}
                      <span className="text-[11px] text-slate-500">
                        Sisa: {formatDecimalNumber(asaDialog.deductions.pelarut.stok)} {asaDialog.deductions.pelarut.satuan}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <p className="text-xs text-slate-500 italic pb-1">
                *Stok akan otomatis berkurang dari sistem saat kamu klik Lanjut.
              </p>
            </div>
            
            <div className="p-4 bg-slate-50 flex justify-end gap-2 border-t border-slate-100 shrink-0">
              <Button
                variant="outline"
                className="text-slate-600 bg-white hover:bg-slate-50 font-bold border-slate-200 rounded-xl"
                onClick={() => setAsaDialog(null)}
              >
                Batal
              </Button>
              <Button
                className="bg-[#8C6208] text-white hover:bg-[#725007] font-bold rounded-xl"
                onClick={async () => {
                  const botols = asaDialog.deductions.botols || [];
                  const pelarut = asaDialog.deductions.pelarut;

                  try {
                    // Deduct each selected botol/fullset
                    for (const botol of botols) {
                      if (botol.qty > 0 && botol.id) {
                        const item = await base44.entities[botol.table].get(botol.id);
                        if (item) {
                          const cur = item.stok || item.stock || 0;
                          await base44.entities[botol.table].update(botol.id, { stok: Math.max(0, cur - botol.qty), stock: Math.max(0, cur - botol.qty) });
                          toast.success(`Stok ${botol.name} terpotong ${botol.qty} pcs!`);
                        }
                      }
                    }

                    // Deduct Pelarut (Superfine/Alkohol)
                    if (pelarut?.vol > 0 && pelarut?.id) {
                      const item = await base44.entities.BahanCair.get(pelarut.id);
                      if (item) {
                        const cur = item.stok || item.stock || 0;
                        const dec = (pelarut.vol * 0.8) / 1000;
                        await base44.entities.BahanCair.update(pelarut.id, { stok: Math.max(0, cur - dec), stock: Math.max(0, cur - dec) });
                        toast.success(`Stok ${pelarut.name} terpotong ${formatDecimalNumber(dec)} kg!`);
                      }
                    }
                  } catch(e) {
                    toast.error('Gagal memotong stok: ' + e.message);
                  }
                  
                  // Update status menjadi 'dikerjakan' (Sedang Dikerjakan) dan tandai stok telah dipotong
                  const updatedProducts = (asaDialog.payload.products || []).map(p => ({
                    ...p,
                    status: (p.status === 'pending' || !p.status) ? 'dikerjakan' : p.status
                  }));

                  const finalPayload = {
                    ...asaDialog.payload,
                    status: 'dikerjakan',
                    products: updatedProducts,
                    stok_terpotong: true,
                    stok_terpotong_at: new Date().toISOString()
                  };

                  proceedSave(finalPayload);
                  setAsaDialog(null);
                }}
              >
                <Check className="w-4 h-4 mr-1.5" /> Lanjut & Potong Stok
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}